/**
 * Currency Constants
 *
 * Master data list for currencies following ISO 4217 standard.
 * Includes formatting information for proper display.
 *
 * Each currency includes:
 * - code: ISO 4217 3-letter code
 * - name: Full currency name
 * - symbol: Currency symbol
 * - locale: Primary locale for formatting (determines decimal/thousand separators)
 * - decimals: Number of decimal places (most are 2, some are 0 or 3)
 */

/**
 * Common currencies used in international trade
 * Ordered by prevalence in global trade
 */
export const CURRENCIES = [
  // Major trading currencies
  { code: 'USD', name: 'US Dollar', symbol: '$', locale: 'en-US', decimals: 2 },
  { code: 'EUR', name: 'Euro', symbol: '€', locale: 'de-DE', decimals: 2 },
  { code: 'GBP', name: 'British Pound', symbol: '£', locale: 'en-GB', decimals: 2 },
  { code: 'JPY', name: 'Japanese Yen', symbol: '¥', locale: 'ja-JP', decimals: 0 },
  { code: 'CNY', name: 'Chinese Yuan', symbol: '¥', locale: 'zh-CN', decimals: 2 },
  { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF', locale: 'de-CH', decimals: 2 },
  { code: 'CAD', name: 'Canadian Dollar', symbol: 'CA$', locale: 'en-CA', decimals: 2 },
  { code: 'AUD', name: 'Australian Dollar', symbol: 'A$', locale: 'en-AU', decimals: 2 },
  { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$', locale: 'zh-HK', decimals: 2 },
  { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$', locale: 'en-SG', decimals: 2 },

  // Other major currencies
  { code: 'NZD', name: 'New Zealand Dollar', symbol: 'NZ$', locale: 'en-NZ', decimals: 2 },
  { code: 'KRW', name: 'South Korean Won', symbol: '₩', locale: 'ko-KR', decimals: 0 },
  { code: 'TWD', name: 'Taiwan Dollar', symbol: 'NT$', locale: 'zh-TW', decimals: 2 },
  { code: 'INR', name: 'Indian Rupee', symbol: '₹', locale: 'en-IN', decimals: 2 },
  { code: 'MXN', name: 'Mexican Peso', symbol: 'MX$', locale: 'es-MX', decimals: 2 },
  { code: 'BRL', name: 'Brazilian Real', symbol: 'R$', locale: 'pt-BR', decimals: 2 },
  { code: 'ZAR', name: 'South African Rand', symbol: 'R', locale: 'en-ZA', decimals: 2 },
  { code: 'RUB', name: 'Russian Ruble', symbol: '₽', locale: 'ru-RU', decimals: 2 },
  { code: 'SEK', name: 'Swedish Krona', symbol: 'kr', locale: 'sv-SE', decimals: 2 },
  { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr', locale: 'nb-NO', decimals: 2 },
  { code: 'DKK', name: 'Danish Krone', symbol: 'kr', locale: 'da-DK', decimals: 2 },
  { code: 'PLN', name: 'Polish Zloty', symbol: 'zł', locale: 'pl-PL', decimals: 2 },
  { code: 'THB', name: 'Thai Baht', symbol: '฿', locale: 'th-TH', decimals: 2 },
  { code: 'MYR', name: 'Malaysian Ringgit', symbol: 'RM', locale: 'ms-MY', decimals: 2 },
  { code: 'IDR', name: 'Indonesian Rupiah', symbol: 'Rp', locale: 'id-ID', decimals: 0 },
  { code: 'PHP', name: 'Philippine Peso', symbol: '₱', locale: 'en-PH', decimals: 2 },
  { code: 'VND', name: 'Vietnamese Dong', symbol: '₫', locale: 'vi-VN', decimals: 0 },
  { code: 'AED', name: 'UAE Dirham', symbol: 'د.إ', locale: 'ar-AE', decimals: 2 },
  { code: 'SAR', name: 'Saudi Riyal', symbol: '﷼', locale: 'ar-SA', decimals: 2 },
  { code: 'TRY', name: 'Turkish Lira', symbol: '₺', locale: 'tr-TR', decimals: 2 },
  { code: 'ILS', name: 'Israeli Shekel', symbol: '₪', locale: 'he-IL', decimals: 2 },
  { code: 'CZK', name: 'Czech Koruna', symbol: 'Kč', locale: 'cs-CZ', decimals: 2 },
  { code: 'HUF', name: 'Hungarian Forint', symbol: 'Ft', locale: 'hu-HU', decimals: 2 },
  { code: 'CLP', name: 'Chilean Peso', symbol: 'CLP$', locale: 'es-CL', decimals: 0 },
  { code: 'COP', name: 'Colombian Peso', symbol: 'COP$', locale: 'es-CO', decimals: 2 },
  { code: 'ARS', name: 'Argentine Peso', symbol: 'ARS$', locale: 'es-AR', decimals: 2 },
  { code: 'PEN', name: 'Peruvian Sol', symbol: 'S/', locale: 'es-PE', decimals: 2 },
  { code: 'EGP', name: 'Egyptian Pound', symbol: 'E£', locale: 'ar-EG', decimals: 2 },
  { code: 'PKR', name: 'Pakistani Rupee', symbol: '₨', locale: 'ur-PK', decimals: 2 },
  { code: 'BDT', name: 'Bangladeshi Taka', symbol: '৳', locale: 'bn-BD', decimals: 2 },
  { code: 'NGN', name: 'Nigerian Naira', symbol: '₦', locale: 'en-NG', decimals: 2 },
  { code: 'KWD', name: 'Kuwaiti Dinar', symbol: 'د.ك', locale: 'ar-KW', decimals: 3 },
  { code: 'BHD', name: 'Bahraini Dinar', symbol: 'د.ب', locale: 'ar-BH', decimals: 3 },
  { code: 'OMR', name: 'Omani Rial', symbol: 'ر.ع.', locale: 'ar-OM', decimals: 3 },
  { code: 'QAR', name: 'Qatari Riyal', symbol: 'ر.ق', locale: 'ar-QA', decimals: 2 },
];

/**
 * Currency lookup map for O(1) access
 */
export const CURRENCY_MAP = Object.freeze(
  CURRENCIES.reduce((map, currency) => {
    map[currency.code] = currency;
    return map;
  }, {})
);

/**
 * Get currency info by code
 * @param {string} code - ISO 4217 currency code
 * @returns {Object|null} Currency info or null if not found
 */
export function getCurrency(code) {
  if (!code) return null;
  return CURRENCY_MAP[code.toUpperCase()] || null;
}

/**
 * Currency options for dropdowns
 * Formatted as { value, label } for use in select elements
 */
export const CURRENCY_OPTIONS = [
  { value: '', label: 'Select currency...' },
  ...CURRENCIES.map(c => ({
    value: c.code,
    label: `${c.code} - ${c.name}`
  }))
];

/**
 * Default currency code
 */
export const DEFAULT_CURRENCY = 'USD';
